import { PlusIcon } from "@xola/icons";
import { Breadcrumb, Button, flash, HeaderToolbar, SubmitButton } from "@xola/ui-kit";
import {
    FormField,
    hasDistributionProfileConfigured,
    InputField,
    requiredEmail,
    requiredString,
    SecondaryButton,
    TextareaField,
} from "common";
import { produce } from "immer";
import React from "react";
import { useMutation } from "react-query";
import { NetworkPreferences, NetworkVisibilityType, Permission, PurchaseRuleLink, Seller } from "types";
import { object } from "yup";
import { XolaFormik } from "../Common/XolaFormik";
import { ContentLengthCounter } from "../Field/ContentLengthCounter";
import { ColumnLayout } from "../Layout/ColumnLayout";
import { ContentLayout } from "../Layout/ContentLayout";
import { AddOperatorDrawer } from "./Partner/AddOperatorDrawer";
import { PartnerDistributionProfile } from "./PartnerDistributionProfile";
import { api } from "@/api/api";
import { ScrollToFieldError } from "@/helpers/validationHelper";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useVisibility } from "@/hooks/useVisibility";
import { Page } from "@/pages/Page";

export const DistributionProfilePage = () => {
    const seller = useSeller();
    const { isGranted } = useAuthorization();

    const { data: termsTemplates } = useAutoFetchCollection<PurchaseRuleLink>("purchaseRuleLinks", {
        "tags._id": "Affiliate",
    });
    const showAddPartner =
        isGranted(Permission.OPERATORS_ACTIONS) &&
        hasDistributionProfileConfigured(seller) &&
        termsTemplates.length > 0;
    const [showAddPartnerDrawer, { toggle: toggleAddPartnerDrawer }] = useVisibility();

    return (
        <Page title="Distribution Profile">
            <ColumnLayout>
                <HeaderToolbar classNames="justify-between">
                    <HeaderToolbar.Breadcrumb>
                        <Breadcrumb.Item>Distribution</Breadcrumb.Item>
                        <Breadcrumb.Item>Distribution Profile</Breadcrumb.Item>
                    </HeaderToolbar.Breadcrumb>
                    {showAddPartner ? (
                        <Button icon={<PlusIcon />} onClick={toggleAddPartnerDrawer}>
                            Add Operator
                        </Button>
                    ) : null}
                </HeaderToolbar>
                <ColumnLayout.Fill>
                    <div>
                        <DistributionProfileForm />
                    </div>
                </ColumnLayout.Fill>
                <AddOperatorDrawer isOpen={showAddPartnerDrawer} onClose={toggleAddPartnerDrawer} />
            </ColumnLayout>
        </Page>
    );
};

const DistributionProfileForm = () => {
    const invalidate = useInvalidate();
    const seller = useSeller();

    const { mutateAsync: saveNetworkPreferences, isLoading: isSaving } = useMutation(
        async (payload: NetworkPreferences) => {
            return api.post(`/sellers/${seller.id}/preferences/network`, payload);
        },
    );

    const handleSubmit = async (values: NetworkPreferences) => {
        await saveNetworkPreferences(values);
        invalidate("sellers", seller.id);
        flash.success("Successfully saved distribution profile");
    };

    return (
        <XolaFormik
            validateOnChange
            validationSchema={getValidationSchema()}
            initialValues={getInitialValues(seller)}
            onSubmit={handleSubmit}
        >
            {({ values, handleSubmit, handleReset }) => {
                const updatedSeller = produce(seller, (seller) => {
                    seller.preferences.network = values;
                });

                return (
                    <ContentLayout isWide isDisabled={isSaving}>
                        <h2>Distribution Profile</h2>
                        <div className="divide-y divide-gray-light pb-5">
                            <ContentLayout.Section
                                title="Business Details"
                                description="These details are displayed when another operator searches for you"
                            >
                                <div className="flex-grow">
                                    <fieldset className="flex-1 space-y-12">
                                        <div className="space-y-4">
                                            <FormField
                                                name="aboutBusiness"
                                                label="About your business"
                                                tooltip="This text will be displayed to other Xola Operators who add you. It will not be displayed to customers"
                                            >
                                                <TextareaField rows={5} />
                                                <ContentLengthCounter value={values.aboutBusiness} maxLength={350} />
                                            </FormField>
                                            <FormField name="phone" label="Distribution Phone Number">
                                                <InputField isRequired />
                                            </FormField>
                                            <FormField name="email" label="Distribution E-mail">
                                                <InputField isRequired />
                                            </FormField>
                                        </div>
                                    </fieldset>
                                </div>
                            </ContentLayout.Section>
                            <ContentLayout.Section
                                title="Profile Preview"
                                description="This is how your business will appear when another operator searches for you"
                            >
                                <div className="flex-grow">
                                    <div className="space-y-4">
                                        <PartnerDistributionProfile seller={updatedSeller} />
                                    </div>
                                </div>
                            </ContentLayout.Section>
                        </div>
                        <ContentLayout.Footer>
                            <SubmitButton isLoading={isSaving} onClick={handleSubmit}>
                                Save
                            </SubmitButton>
                            <SecondaryButton type="reset" onClick={handleReset}>
                                Cancel
                            </SecondaryButton>
                        </ContentLayout.Footer>

                        <ScrollToFieldError />
                    </ContentLayout>
                );
            }}
        </XolaFormik>
    );
};

const getInitialValues = (seller: Seller) => {
    const networkPreferences = seller.preferences.network;
    const { email, phone, aboutBusiness } = networkPreferences ?? {};
    return {
        email: email ?? seller.communicationEmail,
        phone: phone ?? seller.phone,
        aboutBusiness: aboutBusiness ?? "",
        visibilityType: NetworkVisibilityType.VISIBILITY_ALL,
        enabled: true, // we need to enable it when saved
    };
};

const getValidationSchema = () => {
    return object().shape({
        aboutBusiness: requiredString("is required").max(350, "must have less than 350 characters"),
        phone: requiredString("is required").nullable(),
        email: requiredEmail(),
    });
};
